/**
 * useTokens — hook for loading and caching v2 token & network reference data.
 *
 * Fetches the public /api/v2/tokens and /api/v2/networks endpoints once
 * and provides:
 *  - tokens:   V2Token[]       — all active tokens (incl. network info)
 *  - networks: V2Network[]     — all active blockchain networks
 *  - helpers for lookup by id / symbol
 *  - loading / error state
 *  - refresh() to force re-fetch
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { v2GetTokens, v2GetNetworks } from '../services/v2/api';
import type { V2Token, V2Network } from '../types/v2';

export interface UseTokensResult {
  /** All active tokens from the backend */
  tokens: V2Token[];
  /** All active blockchain networks from the backend */
  networks: V2Network[];

  /** Lookup a single token by its UUID */
  getTokenById: (id: string) => V2Token | undefined;
  /** Lookup all tokens that share a symbol (e.g. USDT on multiple networks) */
  getTokensBySymbol: (symbol: string) => V2Token[];
  /** Lookup a network by its UUID */
  getNetworkById: (id: string) => V2Network | undefined;
  /** Lookup a network by its symbol (e.g. "ETH") */
  getNetworkBySymbol: (symbol: string) => V2Network | undefined;

  /** Unique token symbols across all tokens (e.g. ['BTC', 'ETH', 'USDT', ...]) */
  uniqueSymbols: string[];

  /** Whether the initial fetch is in progress */
  loading: boolean;
  /** Error message if the fetch failed */
  error: string | null;
  /** Force re-fetch tokens and networks */
  refresh: () => Promise<void>;
}

export function useTokens(): UseTokensResult {
  const [tokens, setTokens] = useState<V2Token[]>([]);
  const [networks, setNetworks] = useState<V2Network[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tokensRes, networksRes] = await Promise.all([
        v2GetTokens(),
        v2GetNetworks(),
      ]);
      setTokens(tokensRes.tokens);
      setNetworks(networksRes.networks);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load tokens and networks';
      setError(msg);
      console.error('[useTokens] Failed to load reference data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Pre-built lookup maps (re-built only when tokens/networks change)
  const tokenByIdMap = useMemo(() => {
    const map = new Map<string, V2Token>();
    for (const t of tokens) {
      map.set(t.id, t);
    }
    return map;
  }, [tokens]);

  const tokensBySymbolMap = useMemo(() => {
    const map = new Map<string, V2Token[]>();
    for (const t of tokens) {
      const key = t.symbol.toUpperCase();
      const arr = map.get(key) || [];
      arr.push(t);
      map.set(key, arr);
    }
    return map;
  }, [tokens]);

  const networkByIdMap = useMemo(() => {
    const map = new Map<string, V2Network>();
    for (const n of networks) {
      map.set(n.id, n);
    }
    return map;
  }, [networks]);

  const networkBySymbolMap = useMemo(() => {
    const map = new Map<string, V2Network>();
    for (const n of networks) {
      map.set(n.symbol.toUpperCase(), n);
    }
    return map;
  }, [networks]);

  const uniqueSymbols = useMemo(() => {
    return Array.from(new Set(tokens.map(t => t.symbol.toUpperCase())));
  }, [tokens]);

  const getTokenById = useCallback(
    (id: string) => tokenByIdMap.get(id),
    [tokenByIdMap],
  );

  const getTokensBySymbol = useCallback(
    (symbol: string) => tokensBySymbolMap.get(symbol.toUpperCase()) || [],
    [tokensBySymbolMap],
  );

  const getNetworkById = useCallback(
    (id: string) => networkByIdMap.get(id),
    [networkByIdMap],
  );

  const getNetworkBySymbol = useCallback(
    (symbol: string) => networkBySymbolMap.get(symbol.toUpperCase()),
    [networkBySymbolMap],
  );

  return {
    tokens,
    networks,
    getTokenById,
    getTokensBySymbol,
    getNetworkById,
    getNetworkBySymbol,
    uniqueSymbols,
    loading,
    error,
    refresh: fetchData,
  };
}

export default useTokens;
